import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { MapPin, Truck, Clock, RefreshCw, CheckCircle, XCircle, Navigation } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const API = process.env.REACT_APP_BACKEND_URL;

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Custom delivery truck icon
const deliveryIcon = new L.DivIcon({
  className: "custom-marker",
  html: `<div style="background: #ec4899; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3);">
    <svg width="22" height="22" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="1">
      <path d="M1 3h15v13H1V3z"/>
      <path d="M16 8h4l3 4v5h-7V8z"/>
      <circle cx="5.5" cy="18.5" r="2.5" fill="white" stroke="#ec4899" stroke-width="2"/>
      <circle cx="18.5" cy="18.5" r="2.5" fill="white" stroke="#ec4899" stroke-width="2"/>
    </svg>
  </div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
});

export default function TrackingPage() {
  const { bookingId } = useParams();
  const [tracking, setTracking] = useState({ active: false, position: null });
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [delivered, setDelivered] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    const fetchPosition = async () => {
      try {
        const response = await axios.get(`${API}/api/tracking/position`, {
          params: { booking_id: bookingId }
        });
        setTracking(response.data);
        if (response.data.last_update) {
          setLastUpdate(new Date(response.data.last_update));
        }
        if (response.data.delivered) {
          setDelivered(true);
        }
        if (response.data.cancelled) {
          setCancelled(true);
        }
      } catch (error) {
        console.error("Error fetching tracking:", error);
      } finally {
        setLoading(false);
      }
    };

    // Fetch immediately
    fetchPosition();

    // Then fetch every 10 seconds (only if not delivered)
    const interval = setInterval(() => {
      if (!delivered && !cancelled) {
        fetchPosition();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [bookingId, delivered, cancelled]);

  const openInMaps = () => {
    if (tracking.position) {
      const { latitude, longitude } = tracking.position;
      // Open in Google Maps
      window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white p-4">
      <div className="max-w-md mx-auto pt-8">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Truck className="w-10 h-10 text-pink-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Pressing Sakura</h1>
          <p className="text-slate-600">Suivi de livraison</p>
        </div>

        {/* DELIVERED STATE */}
        {delivered ? (
          <Card className="border-green-300 bg-gradient-to-br from-green-50 to-emerald-50">
            <CardContent className="p-8 text-center">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-14 h-14 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-green-800 mb-3">
                Livraison effectuée !
              </h2>
              <p className="text-green-700 mb-6">
                Votre commande a été livrée avec succès. Merci de votre confiance !
              </p>
              <div className="p-4 bg-white/50 rounded-xl">
                <p className="text-sm text-green-600">
                  🌸 À bientôt chez Pressing Sakura !
                </p>
              </div>
            </CardContent>
          </Card>
        ) : cancelled ? (
          /* CANCELLED STATE */
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-12 h-12 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-red-800 mb-2">
                Réservation annulée
              </h2>
              <p className="text-red-700">
                Cette réservation a été annulée. Contactez-nous si vous avez des questions.
              </p>
            </CardContent>
          </Card>
        ) : tracking.active && tracking.position ? (
          <>
            {/* Status Card */}
            <Card className="mb-4 border-green-200 bg-green-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <div>
                    <p className="font-semibold text-green-800">Livreur en route</p>
                    <p className="text-sm text-green-600">Vous êtes le prochain !</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Map Preview */}
            <Card className="mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-pink-500" />
                  Position du livreur
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Interactive Leaflet Map */}
                <div className="w-full h-56 rounded-lg overflow-hidden">
                  <MapContainer
                    center={[tracking.position.latitude, tracking.position.longitude]}
                    zoom={15}
                    style={{ height: "100%", width: "100%" }}
                    zoomControl={true}
                    scrollWheelZoom={false}
                    key={`${tracking.position.latitude}-${tracking.position.longitude}`}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; OpenStreetMap'
                    />
                    <Marker
                      position={[tracking.position.latitude, tracking.position.longitude]}
                      icon={deliveryIcon}
                    >
                      <Popup>
                        <div className="text-center">
                          <strong>🚚 Livreur Pressing Sakura</strong>
                          <br />
                          <span className="text-xs text-gray-500">En route vers vous !</span>
                        </div>
                      </Popup>
                    </Marker>
                  </MapContainer>
                </div>
                
                <div className="mt-3 flex items-center justify-between text-sm text-slate-500">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>
                      Mis à jour {lastUpdate ? `à ${lastUpdate.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}` : "..."}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-green-600">
                    <RefreshCw className="w-4 h-4 animate-spin" style={{ animationDuration: "3s" }} />
                    <span>Auto-refresh</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Open in Maps Button */}
            <button
              onClick={openInMaps}
              className="w-full py-4 bg-pink-500 hover:bg-pink-600 text-white font-semibold rounded-xl shadow-lg transition-colors flex items-center justify-center gap-2"
            >
              <MapPin className="w-5 h-5" />
              Ouvrir dans Google Maps
            </button>
          </>
        ) : (
          /* No active tracking */
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-lg font-semibold text-amber-800 mb-2">
                En attente
              </h2>
              <p className="text-amber-700">
                Le suivi en temps réel n'est pas encore actif. Vous recevrez une notification quand le livreur sera en route vers vous.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 mt-8">
          Pressing Sakura - Service de livraison
        </p>
      </div>
    </div>
  );
}
